import React, { useEffect, useMemo, useRef, useState } from 'react';
import StatusBadge from './StatusBadge';
import { filterCitiesByQuery, resolveSelectedCity } from '../utils/citySearchSelect';

/**
 * Раскрывающийся выбор одного объекта с поиском (графики отделов / директивный).
 */
const CitySearchSelect = ({
  cities = [],
  value = null,
  onChange,
  placeholder = 'Выберите объект',
  className = '',
  testIdPrefix = 'city-search',
  allowEmpty = false,
  emptyLabel = 'Все объекты',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = useMemo(
    () => resolveSelectedCity(cities, value),
    [cities, value]
  );

  const filteredCities = useMemo(
    () => filterCitiesByQuery(cities, query),
    [cities, query]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  if (!cities?.length && !allowEmpty) return null;

  const selectCity = (cityId) => {
    onChange?.(cityId);
    setOpen(false);
    setQuery('');
  };

  const isValueActive = (cityId) => {
    if (value == null || value === '') return false;
    return Number(cityId) === Number(value);
  };

  const isEmptyActive = value == null || value === '';

  return (
    <div className={`po-city-select city-search-select ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className="btn btn-outline-secondary po-city-select__trigger"
        data-testid={`${testIdPrefix}-trigger`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="city-search-select__label">
          {selected ? (
            <>
              <span>{selected.name}</span>
              {selected.current_status && (
                <StatusBadge status={selected.current_status} />
              )}
            </>
          ) : (
            allowEmpty ? emptyLabel : placeholder
          )}
        </span>
        <span className="po-city-select__chevron" aria-hidden>{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="glass-dropdown po-city-select__dropdown" role="listbox">
          <div className="po-city-select__search">
            <input
              ref={searchRef}
              type="search"
              className="form-control"
              placeholder="Поиск по названию…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="po-city-select__list">
            {allowEmpty && !query.trim() && (
              <button
                type="button"
                role="option"
                aria-selected={isEmptyActive}
                data-testid={`${testIdPrefix}-option-all`}
                className={`po-city-select__option city-search-select__option${isEmptyActive ? ' city-search-select__option--active' : ''}`}
                onClick={() => selectCity(null)}
              >
                <span>{emptyLabel}</span>
              </button>
            )}
            {filteredCities.length === 0 ? (
              (query.trim() || !allowEmpty) ? (
                <div className="po-city-select__empty">Ничего не найдено</div>
              ) : null
            ) : (
              filteredCities.map((city) => {
                const isActive = isValueActive(city.id);
                return (
                  <button
                    key={city.id}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-testid={`${testIdPrefix}-option-${city.id}`}
                    className={`po-city-select__option city-search-select__option${isActive ? ' city-search-select__option--active' : ''}`}
                    onClick={() => selectCity(city.id)}
                  >
                    <span>{city.name}</span>
                    {city.current_status && <StatusBadge status={city.current_status} />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySearchSelect;
